import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Target, BookOpen, Users, Zap, MapPin,
  GraduationCap, TrendingUp, Shield, Compass, Lightbulb,
  Phone, CheckCircle, ChevronDown, Globe, Heart, Briefcase,
  Award, Calendar, FileText, Star, UserCheck, ArrowUpRight,
  Mail, Tent, Crown, Flame
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { MarketingNav, MarketingFooter } from '../components/MarketingLayout';
import { SEO } from '../components/SEO';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { api } from '../api';
import { analytics } from '../hooks/useAnalytics';

const HERO_IMG = "https://images.unsplash.com/photo-1523050854058-8df90110c476?auto=format&fit=crop&w=1920&q=80";
const BRIDGE_IMG = "https://images.unsplash.com/photo-1659356874140-166861df06da?auto=format&fit=crop&w=1200&q=80";

function AnimSection({ children, className = '', animation = 'scroll-fade-up' }) {
  const [ref, isVisible] = useScrollReveal(0.12);
  return (
    <div ref={ref} className={`${animation} ${isVisible ? 'is-visible' : ''} ${className}`}>
      {children}
    </div>
  );
}

function StaggerGrid({ children, className = '' }) {
  const [ref, isVisible] = useScrollReveal(0.1);
  return (
    <div ref={ref} className={`stagger-children ${isVisible ? 'is-visible' : ''} ${className}`}>
      {children}
    </div>
  );
}

/* ─── SECTION 1: HERO ─── */
function HeroSection() {
  return (
    <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden" data-testid="elite-hero">
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-[#0F392B]" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0F392B]/95 via-[#0F392B]/80 to-[#0F392B]" />
      </div>
      <div className="relative z-10 max-w-4xl mx-auto px-6 text-center py-32">
        <p className="text-[#D4AF37] uppercase tracking-[0.25em] text-xs font-medium mb-8"
          style={{ animation: 'fadeIn 0.8s ease forwards', animationDelay: '0.2s', opacity: 0 }}>
          Ivy League Consulting
        </p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-8"
          style={{ fontFamily: "'Playfair Display', serif", animation: 'fadeIn 0.8s ease forwards', animationDelay: '0.4s', opacity: 0 }}>
          You Don't Need an Ivy League Acceptance to Build an Ivy League Future.
        </h1>
        <p className="text-lg sm:text-xl text-white/70 max-w-2xl mx-auto mb-12 leading-relaxed"
          style={{ animation: 'fadeIn 0.8s ease forwards', animationDelay: '0.6s', opacity: 0 }}>
          Most students don't apply to Harvard.<br />
          But the most successful students think, plan, and execute like those who do.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4"
          style={{ animation: 'fadeIn 0.8s ease forwards', animationDelay: '0.8s', opacity: 0 }}>
          <Link to="/sign-up">
            <Button size="lg" className="bg-[#D4AF37] hover:bg-[#C5A028] text-[#0F392B] font-semibold px-8 h-13 text-base" data-testid="hero-build-plan-btn">
              Build My Plan <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
          <a href="#pathways">
            <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 px-8 h-13 text-base" data-testid="hero-see-path-btn">
              See My Path Forward <ChevronDown className="ml-2 h-5 w-5" />
            </Button>
          </a>
        </div>
      </div>
      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-white to-transparent" />
    </section>
  );
}

/* ─── SECTION 1.5: BUILD YOUR OWN TENT (DELINEATION) ─── */
function TentSection() {
  return (
    <section className="py-20 sm:py-28 bg-white relative overflow-hidden" data-testid="elite-tent">
      <div className="max-w-6xl mx-auto px-6">
        <AnimSection className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-red-50 border border-red-200 rounded-full px-5 py-2 mb-6">
            <Flame className="h-4 w-4 text-red-500" />
            <span className="text-red-600 text-sm font-semibold tracking-wide">Real Talk</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0F392B] leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
            Stop Falling Into Someone Else's Tent.
          </h2>
          <p className="text-lg text-gray-500 mt-4 max-w-2xl mx-auto">
            The Ivy League dream sells itself. But the question isn't whether Harvard is worth it — it's whether chasing the wrong goal costs you the right one.
          </p>
        </AnimSection>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {/* Left: Ivy Track */}
          <AnimSection animation="scroll-fade-left">
            <div className="relative h-full rounded-2xl border-2 border-[#0F392B]/10 bg-[#FAFAF8] p-8 sm:p-10 group hover:border-[#0F392B]/30 transition-all duration-300" data-testid="tent-ivy-track">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-11 h-11 rounded-xl bg-[#0F392B] flex items-center justify-center">
                  <Crown className="h-5 w-5 text-[#D4AF37]" />
                </div>
                <div>
                  <p className="text-xs text-[#D4AF37] uppercase tracking-widest font-medium">The Ivy Track</p>
                  <h3 className="text-xl font-bold text-[#0F392B]" style={{ fontFamily: "'Playfair Display', serif" }}>Chasing the Ivy League?</h3>
                </div>
              </div>
              <p className="text-gray-600 leading-relaxed mb-6">
                You've got the stats, the ambition, and the willingness to compete at the highest level. If the Ivy League is your target — we'll engineer a case that gets you in.
              </p>
              <ul className="space-y-2.5 mb-8">
                {["3.8+ GPA, strong test scores", "Clear intellectual direction", "Ready for elite-level positioning"].map((item, i) => (
                  <li key={i} className="flex items-center gap-2.5 text-sm text-gray-600">
                    <CheckCircle className="h-4 w-4 text-[#0F392B] shrink-0" /> {item}
                  </li>
                ))}
              </ul>
              <Link to="/pricing">
                <Button className="w-full bg-[#0F392B] hover:bg-[#0F392B]/90 text-white font-semibold" data-testid="tent-ivy-cta">
                  See Consulting Packages <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </AnimSection>

          {/* Right: Build Your Own Tent */}
          <AnimSection animation="scroll-fade-right">
            <div className="relative h-full rounded-2xl border-2 border-[#D4AF37] bg-[#0F392B] p-8 sm:p-10 group hover:shadow-2xl transition-all duration-300" data-testid="tent-own-track">
              <div className="absolute -top-3 right-6 bg-[#D4AF37] text-[#0F392B] text-xs font-bold uppercase tracking-wider px-4 py-1.5 rounded-full">
                The Real Play
              </div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-11 h-11 rounded-xl bg-[#D4AF37]/20 flex items-center justify-center">
                  <Tent className="h-5 w-5 text-[#D4AF37]" />
                </div>
                <div>
                  <p className="text-xs text-[#D4AF37] uppercase tracking-widest font-medium">Your Tent</p>
                  <h3 className="text-xl font-bold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>Let's Build Our Own.</h3>
                </div>
              </div>
              <p className="text-white/70 leading-relaxed mb-6">
                Not everyone needs the Ivy League brand. But everyone deserves Ivy-level strategy, mentorship, and outcomes. We'll build a path that's <span className="text-[#D4AF37] font-semibold">yours</span> — not a knockoff of someone else's.
              </p>
              <ul className="space-y-2.5 mb-8">
                {["Any GPA, any starting point", "Community college, state school, or hybrid", "Strategy that turns your story into an advantage"].map((item, i) => (
                  <li key={i} className="flex items-center gap-2.5 text-sm text-white/70">
                    <CheckCircle className="h-4 w-4 text-[#D4AF37] shrink-0" /> {item}
                  </li>
                ))}
              </ul>
              <a href="#pathways">
                <Button className="w-full bg-[#D4AF37] hover:bg-[#C5A028] text-[#0F392B] font-semibold" data-testid="tent-own-cta">
                  See My Path Forward <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </a>
            </div>
          </AnimSection>
        </div>

        <AnimSection className="mt-12 text-center">
          <p className="text-gray-400 text-sm italic">
            The school on your diploma matters less than the strategy behind your next decade.
          </p>
        </AnimSection>
      </div>
    </section>
  );
}

/* ─── SECTION 2: THE REALITY (DISARM) ─── */
function RealitySection() {
  return (
    <section className="py-24 sm:py-32 bg-white" data-testid="elite-reality">
      <div className="max-w-3xl mx-auto px-6">
        <AnimSection>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#0F392B] mb-8 text-center" style={{ fontFamily: "'Playfair Display', serif" }}>
            Most Students Are Undersold — Not Underqualified.
          </h2>
        </AnimSection>
        <AnimSection>
          <p className="text-lg text-gray-600 leading-relaxed mb-8">
            The gap between where students end up and where they could have landed isn't about intelligence or work ethic.
            It's about the system they were given — and the system almost always underdelivers.
          </p>
          <ul className="space-y-4 mb-10">
            {[
              "Generic guidance systems that treat every student the same — regardless of talent, ambition, or circumstance.",
              "A complete lack of strategic planning — where applications are assembled, not engineered.",
              "Missed potential due to lack of exposure — students don't know what's possible because nobody showed them."
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-3">
                <div className="mt-1.5 w-1.5 h-1.5 bg-[#D4AF37] rounded-full shrink-0" />
                <p className="text-gray-600 leading-relaxed">{item}</p>
              </li>
            ))}
          </ul>
          <div className="border-l-4 border-[#D4AF37] pl-6 py-2">
            <p className="text-xl text-[#0F392B] font-medium italic" style={{ fontFamily: "'Playfair Display', serif" }}>
              "No one is overqualified. No one should feel underqualified."
            </p>
          </div>
        </AnimSection>
      </div>
    </section>
  );
}

/* ─── SECTION 3: IVY-LEVEL STRATEGY ─── */
const strategyCards = [
  { icon: Target, title: "Positioning vs School Selection", desc: "It's not about finding the right school. It's about making every school see you as the right student." },
  { icon: BookOpen, title: "Narrative vs Activity Listing", desc: "Your application tells a story. We make sure every line reinforces the same compelling thesis." },
  { icon: Zap, title: "Engineered Opportunities vs Passive Participation", desc: "Elite applicants don't stumble into achievements. They strategically build a portfolio that signals intent." },
  { icon: Compass, title: "Strategic Roadmap vs Guessing", desc: "Month-by-month execution beats last-minute panic. We build your path before you walk it." },
];

function StrategySection() {
  return (
    <section className="py-24 sm:py-32 bg-[#FAFAF8]" data-testid="elite-strategy-cards">
      <div className="max-w-6xl mx-auto px-6">
        <AnimSection className="text-center mb-16">
          <p className="text-[#D4AF37] uppercase tracking-[0.2em] text-xs font-medium mb-4">The Framework</p>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#0F392B]" style={{ fontFamily: "'Playfair Display', serif" }}>
            What "Ivy-Level Strategy" Actually Means
          </h2>
        </AnimSection>
        <StaggerGrid className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {strategyCards.map((c, i) => (
            <div key={i} className="bg-white rounded-xl p-8 border border-gray-100 hover:border-[#D4AF37]/30 transition-all duration-300 hover:shadow-lg group" data-testid={`strategy-card-${i}`}>
              <div className="w-12 h-12 rounded-lg bg-[#0F392B]/5 flex items-center justify-center mb-5 group-hover:bg-[#0F392B]/10 transition-colors">
                <c.icon className="h-6 w-6 text-[#0F392B]" />
              </div>
              <h3 className="text-lg font-semibold text-[#0F392B] mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>{c.title}</h3>
              <p className="text-gray-600 leading-relaxed text-sm">{c.desc}</p>
            </div>
          ))}
        </StaggerGrid>
      </div>
    </section>
  );
}

/* ─── SECTION 4: PATHWAYS ─── */
const pathways = [
  {
    icon: TrendingUp, label: "Path 1",
    title: "Community College → Transfer",
    points: ["GPA dominance strategy", "Strategic transfer targeting", "Application narrative positioning", "Articulation agreement mapping"],
    color: "#0F392B"
  },
  {
    icon: Award, label: "Path 2",
    title: "State School Optimization",
    points: ["Leadership pipeline building", "Targeted internship strategy", "Honors program positioning", "Graduate school preparation"],
    color: "#D4AF37"
  },
  {
    icon: Globe, label: "Path 3",
    title: "Hybrid / Non-Traditional",
    points: ["Strategic gap years", "Work experience leveraging", "Portfolio building", "Alternative credential pathways"],
    color: "#0F392B"
  }
];

function PathwaysSection() {
  return (
    <section id="pathways" className="py-24 sm:py-32 bg-white" data-testid="elite-pathways">
      <div className="max-w-6xl mx-auto px-6">
        <AnimSection className="text-center mb-16">
          <p className="text-[#D4AF37] uppercase tracking-[0.2em] text-xs font-medium mb-4">Multiple Routes, One Standard</p>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#0F392B] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            There's More Than One Way to Win
          </h2>
        </AnimSection>
        <StaggerGrid className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {pathways.map((p, i) => (
            <div key={i} className="relative bg-white rounded-xl border border-gray-100 overflow-hidden hover:shadow-xl transition-all duration-300 group" data-testid={`pathway-card-${i}`}>
              <div className="h-1.5 w-full" style={{ backgroundColor: p.color }} />
              <div className="p-8">
                <div className="w-11 h-11 rounded-lg flex items-center justify-center mb-4" style={{ backgroundColor: `${p.color}10` }}>
                  <p.icon className="h-5 w-5" style={{ color: p.color }} />
                </div>
                <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">{p.label}</p>
                <h3 className="text-xl font-bold text-[#0F392B] mb-5" style={{ fontFamily: "'Playfair Display', serif" }}>{p.title}</h3>
                <ul className="space-y-3">
                  {p.points.map((pt, j) => (
                    <li key={j} className="flex items-center gap-2.5 text-sm text-gray-600">
                      <CheckCircle className="h-4 w-4 text-[#D4AF37] shrink-0" />
                      {pt}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </StaggerGrid>
        <AnimSection className="mt-12 text-center">
          <p className="text-xl text-[#0F392B] font-medium italic" style={{ fontFamily: "'Playfair Display', serif" }}>
            "The school matters less than what you do with it."
          </p>
        </AnimSection>
      </div>
    </section>
  );
}

/* ─── PATHWAY QUIZ (Advanced Feature 1) ─── */
const majorOptions = [
  "Computer Science", "Engineering", "Pre-Med / Biology", "Business / Economics",
  "Pre-Law / Political Science", "Humanities / English", "Arts / Design",
  "Psychology", "Communications", "Undecided"
];

function PathwayQuiz() {
  const [gpa, setGpa] = useState('');
  const [major, setMajor] = useState('');
  const [budget, setBudget] = useState('');
  const [timeline, setTimeline] = useState('');
  const [result, setResult] = useState(null);
  const [leadEmail, setLeadEmail] = useState('');
  const [leadName, setLeadName] = useState('');
  const [leadCaptured, setLeadCaptured] = useState(false);
  const [leadLoading, setLeadLoading] = useState(false);

  const getResult = (e) => {
    e.preventDefault();
    const gpaVal = parseFloat(gpa);
    let track, explanation, nextSteps;

    if (!gpa || !major || !budget || !timeline) return;

    if (gpaVal >= 3.7 && budget === 'flexible' && (timeline === 'freshman' || timeline === 'sophomore')) {
      track = "Direct Admission — Elite Track";
      explanation = "Your academic profile and timeline give you a strong runway for direct admission to top-tier institutions. With strategic positioning, you can compete at the highest level.";
      nextSteps = ["Begin narrative development and essay architecture", "Build a strategic extracurricular portfolio", "Target reach, match, and safety schools with precision"];
    } else if (gpaVal >= 3.5 && budget !== 'tight') {
      track = "State School Optimization";
      explanation = "Your profile is strong. By maximizing leadership, honors programs, and targeted internships at a well-chosen state school, you'll build an elite-level outcome without the elite price tag.";
      nextSteps = ["Identify state schools with strong programs in " + major, "Map honors program requirements and deadlines", "Build a leadership and internship pipeline"];
    } else if (gpaVal >= 3.0 || (budget === 'tight' && timeline !== 'senior')) {
      track = "Community College → Transfer";
      explanation = "The transfer path is one of the most underutilized strategies in higher education. Dominate your GPA, then transfer into a top-tier institution with a compelling narrative of growth.";
      nextSteps = ["Research CC-to-university articulation agreements", "Focus on GPA dominance in your first year", "Build your transfer application narrative now"];
    } else {
      track = "Hybrid / Non-Traditional";
      explanation = "Your path doesn't fit the conventional mold — and that's an advantage. Gap years, work experience, and portfolio building create the kind of story admissions officers remember.";
      nextSteps = ["Explore meaningful gap year or work experiences", "Build a portfolio that demonstrates real-world impact", "Develop your unique narrative around resilience and growth"];
    }

    setResult({ track, explanation, nextSteps });
    analytics.quizComplete(track, major);
  };

  return (
    <section className="py-20 bg-[#0F392B]" data-testid="pathway-quiz">
      <div className="max-w-3xl mx-auto px-6">
        <AnimSection className="text-center mb-12">
          <p className="text-[#D4AF37] uppercase tracking-[0.2em] text-xs font-medium mb-4">Interactive Tool</p>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
            What's Your Best Path?
          </h2>
          <p className="text-white/60">Answer four questions. Get a strategic recommendation.</p>
        </AnimSection>

        {!result ? (
          <AnimSection>
            <form onSubmit={getResult} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 space-y-6">
              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">Current GPA (unweighted)</label>
                <input type="number" step="0.1" min="0" max="4.0" value={gpa} onChange={(e) => setGpa(e.target.value)}
                  placeholder="e.g. 3.5" className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:border-[#D4AF37] transition-colors" data-testid="quiz-gpa" />
              </div>
              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">Intended Major</label>
                <select value={major} onChange={(e) => setMajor(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white focus:outline-none focus:border-[#D4AF37] transition-colors" data-testid="quiz-major">
                  <option value="" className="text-gray-900">Select your intended major</option>
                  {majorOptions.map(m => <option key={m} value={m} className="text-gray-900">{m}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">Budget Sensitivity</label>
                <div className="grid grid-cols-3 gap-3">
                  {[{ v: 'tight', l: 'Cost-Conscious' }, { v: 'moderate', l: 'Moderate' }, { v: 'flexible', l: 'Flexible' }].map(b => (
                    <button key={b.v} type="button" onClick={() => setBudget(b.v)}
                      className={`py-3 rounded-lg text-sm font-medium border transition-all ${budget === b.v ? 'bg-[#D4AF37] text-[#0F392B] border-[#D4AF37]' : 'bg-white/5 text-white/70 border-white/20 hover:border-white/40'}`}
                      data-testid={`quiz-budget-${b.v}`}>
                      {b.l}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">Where Are You in Your Timeline?</label>
                <div className="grid grid-cols-2 gap-3">
                  {[{ v: 'freshman', l: 'Freshman / Sophomore' }, { v: 'sophomore', l: 'Junior' }, { v: 'senior', l: 'Senior' }, { v: 'gap', l: 'Gap Year / Transfer' }].map(t => (
                    <button key={t.v} type="button" onClick={() => setTimeline(t.v)}
                      className={`py-3 rounded-lg text-sm font-medium border transition-all ${timeline === t.v ? 'bg-[#D4AF37] text-[#0F392B] border-[#D4AF37]' : 'bg-white/5 text-white/70 border-white/20 hover:border-white/40'}`}
                      data-testid={`quiz-timeline-${t.v}`}>
                      {t.l}
                    </button>
                  ))}
                </div>
              </div>
              <Button type="submit" size="lg" className="w-full bg-[#D4AF37] hover:bg-[#C5A028] text-[#0F392B] font-semibold h-13" data-testid="quiz-submit-btn">
                Show My Path <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </form>
          </AnimSection>
        ) : (
          <AnimSection>
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8" data-testid="quiz-result">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/20 flex items-center justify-center">
                  <Compass className="h-5 w-5 text-[#D4AF37]" />
                </div>
                <div>
                  <p className="text-xs text-white/50 uppercase tracking-wider">Your Recommended Path</p>
                  <h3 className="text-xl font-bold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>{result.track}</h3>
                </div>
              </div>
              <p className="text-white/70 leading-relaxed mb-6">{result.explanation}</p>
              <div className="mb-8">
                <p className="text-sm font-medium text-white/80 mb-3">Immediate Next Steps:</p>
                <ul className="space-y-2">
                  {result.nextSteps.map((s, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-white/60">
                      <CheckCircle className="h-4 w-4 text-[#D4AF37] mt-0.5 shrink-0" />
                      {s}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/sign-up" className="flex-1">
                  <Button size="lg" className="w-full bg-[#D4AF37] hover:bg-[#C5A028] text-[#0F392B] font-semibold" data-testid="quiz-result-cta">
                    Start My Strategy <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10" onClick={() => { setResult(null); setLeadCaptured(false); }} data-testid="quiz-retake">
                  Retake Quiz
                </Button>
              </div>

              {/* Lead Capture Form */}
              {!leadCaptured ? (
                <div className="mt-8 pt-6 border-t border-white/10" data-testid="quiz-lead-form">
                  <div className="flex items-center gap-2 mb-3">
                    <Mail className="h-4 w-4 text-[#D4AF37]" />
                    <p className="text-sm font-medium text-white/80">Get your personalized strategy guide via email</p>
                  </div>
                  <div className="space-y-3">
                    <input type="text" value={leadName} onChange={(e) => setLeadName(e.target.value)}
                      placeholder="Your first name" className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:border-[#D4AF37] transition-colors text-sm" data-testid="quiz-lead-name" />
                    <input type="email" value={leadEmail} onChange={(e) => setLeadEmail(e.target.value)}
                      placeholder="Your email address" className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:border-[#D4AF37] transition-colors text-sm" data-testid="quiz-lead-email" />
                    <Button size="lg" className="w-full bg-white/10 hover:bg-white/20 text-white border border-white/20 font-medium"
                      disabled={leadLoading || !leadEmail}
                      onClick={async () => {
                        if (!leadEmail) return;
                        setLeadLoading(true);
                        try {
                          await api.captureLead({
                            email: leadEmail,
                            name: leadName,
                            source: "elite_strategy_quiz",
                            quiz_data: { track: result.track, gpa, major, budget, timeline }
                          });
                          setLeadCaptured(true);
                          analytics.leadCapture('elite_strategy_quiz', leadEmail);
                        } catch (e) { console.error(e); }
                        setLeadLoading(false);
                      }}
                      data-testid="quiz-lead-submit">
                      {leadLoading ? 'Sending...' : 'Send My Strategy Guide'} <Mail className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="mt-8 pt-6 border-t border-white/10 text-center" data-testid="quiz-lead-success">
                  <CheckCircle className="h-8 w-8 text-[#D4AF37] mx-auto mb-2" />
                  <p className="text-white font-medium">You're in, {leadName || 'friend'}.</p>
                  <p className="text-white/50 text-sm mt-1">We'll be in touch with your personalized strategy guide.</p>
                </div>
              )}
            </div>
          </AnimSection>
        )}
      </div>
    </section>
  );
}

/* ─── SECTION 5: OPPORTUNITY ATLAS ─── */
function OpportunityAtlasSection() {
  const [zipInput, setZipInput] = useState('');

  return (
    <section className="py-24 sm:py-32 bg-white" data-testid="elite-atlas">
      <div className="max-w-6xl mx-auto px-6">
        <AnimSection className="text-center mb-16">
          <p className="text-[#D4AF37] uppercase tracking-[0.2em] text-xs font-medium mb-4">Data-Driven Insight</p>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#0F392B] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Your ZIP Code Shapes Outcomes — But It Doesn't Define Them
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Based on millions of data points tracking life outcomes, the Opportunity Atlas reveals
            dramatic differences in income, mobility, and opportunity — by neighborhood.
          </p>
        </AnimSection>

        <AnimSection>
          <div className="relative rounded-2xl overflow-hidden border border-gray-200 shadow-lg bg-gray-50">
            <iframe
              src="https://www.opportunityatlas.org/"
              title="Opportunity Atlas"
              className="w-full h-[500px] sm:h-[600px] border-0"
              loading="lazy"
              sandbox="allow-scripts allow-same-origin allow-popups"
              data-testid="atlas-iframe"
            />
            <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-white/80 via-transparent to-transparent flex items-end justify-center pb-6">
              <div className="pointer-events-auto bg-white rounded-xl shadow-xl border border-gray-100 p-6 max-w-lg mx-4 text-center">
                <MapPin className="h-6 w-6 text-[#D4AF37] mx-auto mb-3" />
                <p className="text-[#0F392B] font-semibold mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Enter your ZIP code.
                </p>
                <p className="text-sm text-gray-500 mb-4">
                  Now imagine navigating college admissions without guidance.
                </p>
                <a href="https://www.opportunityatlas.org/" target="_blank" rel="noopener noreferrer">
                  <Button size="sm" className="bg-[#0F392B] hover:bg-[#0F392B]/90 text-white" data-testid="atlas-explore-btn">
                    Explore the Atlas <ArrowUpRight className="ml-1.5 h-4 w-4" />
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </AnimSection>
      </div>
    </section>
  );
}

/* ─── SECTION 6: BRIDGE DATA → SERVICE ─── */
function BridgeSection() {
  return (
    <section className="py-24 sm:py-32 bg-[#FAFAF8]" data-testid="elite-bridge">
      <div className="max-w-4xl mx-auto px-6 text-center">
        <AnimSection>
          <p className="text-[#D4AF37] uppercase tracking-[0.2em] text-xs font-medium mb-4">The Solution</p>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#0F392B] mb-8" style={{ fontFamily: "'Playfair Display', serif" }}>
            Data Shows the Gap. We Close It.
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed mb-8">
            The Opportunity Atlas exposes the disparities. Ivy League Consulting provides the structure,
            access, and strategic execution to overcome them — regardless of starting point.
          </p>
          <div className="border-l-4 border-[#D4AF37] pl-6 py-2 text-left max-w-xl mx-auto">
            <p className="text-2xl text-[#0F392B] font-medium italic" style={{ fontFamily: "'Playfair Display', serif" }}>
              "Talent is everywhere. Strategy is not."
            </p>
          </div>
        </AnimSection>
      </div>
    </section>
  );
}

/* ─── SECTION 7: WHAT USERS GET ─── */
const userBenefits = [
  { icon: Compass, text: "Structured roadmap — month-by-month execution plan" },
  { icon: Briefcase, text: "Resume positioning — strategic framing for maximum impact" },
  { icon: FileText, text: "Essay and story strategy — crafted narratives that resonate" },
  { icon: Star, text: "Scholarship optimization — maximize financial opportunities" },
  { icon: Users, text: "Mentorship + exposure — connect with those who've been there" },
  { icon: Shield, text: "Accountability system — stay on track, hit every deadline" },
];

function BenefitsSection() {
  return (
    <section className="py-24 sm:py-32 bg-white" data-testid="elite-benefits">
      <div className="max-w-4xl mx-auto px-6">
        <AnimSection className="text-center mb-16">
          <p className="text-[#D4AF37] uppercase tracking-[0.2em] text-xs font-medium mb-4">What You Get</p>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#0F392B]" style={{ fontFamily: "'Playfair Display', serif" }}>
            You Get Access to the Same Playbook Elite Students Use
          </h2>
        </AnimSection>
        <StaggerGrid className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {userBenefits.map((b, i) => (
            <div key={i} className="flex items-start gap-4 p-5 rounded-xl bg-[#FAFAF8] border border-gray-100 hover:border-[#D4AF37]/30 transition-all" data-testid={`benefit-${i}`}>
              <div className="w-10 h-10 rounded-lg bg-[#0F392B]/5 flex items-center justify-center shrink-0">
                <b.icon className="h-5 w-5 text-[#0F392B]" />
              </div>
              <p className="text-gray-700 leading-relaxed pt-1.5">{b.text}</p>
            </div>
          ))}
        </StaggerGrid>
      </div>
    </section>
  );
}

/* ─── SECTION 8: IVY BRIDGE PROGRAM™ ─── */
function IvyBridgeSection() {
  return (
    <section className="py-24 sm:py-32 bg-[#0F392B] relative overflow-hidden" data-testid="elite-ivy-bridge">
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '32px 32px' }} />
      </div>
      <div className="relative max-w-6xl mx-auto px-6">
        <AnimSection className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full px-5 py-2 mb-6">
            <Heart className="h-4 w-4 text-[#D4AF37]" />
            <span className="text-[#D4AF37] text-sm font-medium">Flagship Feature</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
            You Don't Just Get a Plan.<br className="hidden sm:block" />
            You Get a Person Who's Already Living It.
          </h2>
          <p className="text-lg text-white/60 max-w-2xl mx-auto">
            Every student is paired with an Ivy League student studying the same intended field.
          </p>
        </AnimSection>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
          <AnimSection animation="scroll-fade-left">
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img src={BRIDGE_IMG} alt="Mentorship connection" className="w-full h-80 object-cover" loading="lazy" />
            </div>
          </AnimSection>
          <AnimSection animation="scroll-fade-right">
            <StaggerGrid className="space-y-5">
              {[
                { icon: UserCheck, title: "Major-Based Matching", desc: "Paired with an Ivy student in your exact field of interest." },
                { icon: Phone, title: "Real Conversations", desc: "Monthly 1:1 calls — not automated advice, real human connection." },
                { icon: Lightbulb, title: "Exposure to Elite Thinking", desc: "Learn how top students approach academics, research, and opportunities." },
                { icon: TrendingUp, title: "Direct Success Pathway Insight", desc: "Internship tips, course selection, grad school prep — from someone who's done it." },
              ].map((f, i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center shrink-0">
                    <f.icon className="h-5 w-5 text-[#D4AF37]" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-1">{f.title}</h4>
                    <p className="text-white/50 text-sm leading-relaxed">{f.desc}</p>
                  </div>
                </div>
              ))}
            </StaggerGrid>
          </AnimSection>
        </div>

        {/* Alignment Block */}
        <AnimSection className="text-center mb-16">
          <h3 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            This Isn't Mentorship. This Is Alignment.
          </h3>
          <p className="text-white/60 max-w-xl mx-auto leading-relaxed mb-6">
            We remove barriers created by geography, resources, and access.
          </p>
          <div className="border-l-4 border-[#D4AF37] pl-6 py-2 text-left max-w-xl mx-auto">
            <p className="text-xl text-white/80 italic" style={{ fontFamily: "'Playfair Display', serif" }}>
              "Not to compare. Not to intimidate. But to connect potential with proof."
            </p>
          </div>
        </AnimSection>

        {/* Practical Value */}
        <AnimSection>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-16">
            {[
              { icon: Calendar, label: "Monthly 1:1 Calls" },
              { icon: GraduationCap, label: "Academic Guidance" },
              { icon: Briefcase, label: "Internship Insights" },
              { icon: Lightbulb, label: "Real-World Advice" },
            ].map((v, i) => (
              <div key={i} className="text-center p-5 rounded-xl bg-white/5 border border-white/10">
                <v.icon className="h-6 w-6 text-[#D4AF37] mx-auto mb-2" />
                <p className="text-white/70 text-sm font-medium">{v.label}</p>
              </div>
            ))}
          </div>
        </AnimSection>

        {/* Micro-conversion + CTA */}
        <AnimSection className="text-center">
          <p className="text-lg text-white/50 italic mb-8" style={{ fontFamily: "'Playfair Display', serif" }}>
            "Imagine having someone one step ahead of you — just showing you the way."
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/sign-up">
              <Button size="lg" className="bg-[#D4AF37] hover:bg-[#C5A028] text-[#0F392B] font-semibold px-8" data-testid="bridge-get-matched-btn">
                Get Matched <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <a href="tel:2027313100">
              <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 px-8" data-testid="bridge-connect-btn">
                Connect Me to My Future <Phone className="ml-2 h-4 w-4" />
              </Button>
            </a>
          </div>
        </AnimSection>
      </div>
    </section>
  );
}

/* ─── IVY BRIDGE MATCH PREVIEW (Advanced Feature 2) ─── */
const ivyMentorProfiles = {
  "Computer Science": { school: "MIT", year: "Junior", focus: "AI/ML Research & Systems", achievement: "Published research at top ML conference", interests: "Hackathons, open-source contributing, startup culture" },
  "Engineering": { school: "Stanford", year: "Senior", focus: "Mechanical & Aerospace Engineering", achievement: "NASA internship, led senior design project", interests: "Robotics club, Formula SAE, sustainability tech" },
  "Pre-Med / Biology": { school: "Johns Hopkins", year: "Junior", focus: "Biomedical Sciences & Research", achievement: "NIH Summer Research Fellowship", interests: "Clinical volunteering, bioethics, global health" },
  "Business / Economics": { school: "UPenn (Wharton)", year: "Senior", focus: "Finance & Entrepreneurship", achievement: "Founded campus venture fund, Goldman Sachs internship", interests: "Venture capital, case competitions, fintech" },
  "Pre-Law / Political Science": { school: "Yale", year: "Junior", focus: "Constitutional Law & Policy", achievement: "Published in Yale Journal of Law & Society", interests: "Mock trial, student government, policy research" },
  "Humanities / English": { school: "Harvard", year: "Senior", focus: "Comparative Literature & Creative Writing", achievement: "Fulbright semifinalist, published in literary journals", interests: "The Harvard Advocate, creative workshops, translation" },
  "Arts / Design": { school: "RISD / Brown", year: "Junior", focus: "Graphic Design & Digital Media", achievement: "Work featured in RISD Museum student exhibition", interests: "Design thinking, interdisciplinary studios, typography" },
  "Psychology": { school: "Columbia", year: "Senior", focus: "Cognitive & Clinical Psychology", achievement: "Lead RA in developmental psychology lab", interests: "Mental health advocacy, neuroimaging research, peer counseling" },
  "Communications": { school: "Northwestern", year: "Junior", focus: "Journalism & Strategic Communications", achievement: "Published in The Daily Northwestern, PR internship at Edelman", interests: "Podcasting, media ethics, documentary filmmaking" },
  "Undecided": { school: "Princeton", year: "Sophomore", focus: "Exploring Liberal Arts & Sciences", achievement: "Dean's List, interdisciplinary research grant", interests: "Debate team, community service, exploring multiple departments" },
};

function IvyBridgePreview() {
  const [selectedMajor, setSelectedMajor] = useState('');
  const profile = ivyMentorProfiles[selectedMajor];

  return (
    <section className="py-20 bg-[#FAFAF8]" data-testid="bridge-preview">
      <div className="max-w-3xl mx-auto px-6">
        <AnimSection className="text-center mb-10">
          <p className="text-[#D4AF37] uppercase tracking-[0.2em] text-xs font-medium mb-4">Preview Your Match</p>
          <h2 className="text-3xl font-bold text-[#0F392B] mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
            See Who You Could Be Paired With
          </h2>
          <p className="text-gray-600">Select your intended major to preview a mentor profile.</p>
        </AnimSection>

        <AnimSection>
          <div className="max-w-md mx-auto mb-8">
            <select value={selectedMajor} onChange={(e) => { setSelectedMajor(e.target.value); if (e.target.value) analytics.bridgePreview(e.target.value); }}
              className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-white text-[#0F392B] focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition-all"
              data-testid="bridge-major-select">
              <option value="">Choose your intended major...</option>
              {majorOptions.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>

          {profile && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-lg overflow-hidden" data-testid="bridge-profile-card">
              <div className="bg-[#0F392B] px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                    <GraduationCap className="h-5 w-5 text-[#D4AF37]" />
                  </div>
                  <div>
                    <p className="text-white font-semibold">{profile.school} — {profile.year}</p>
                    <p className="text-white/50 text-xs">Ivy Bridge Mentor</p>
                  </div>
                </div>
                <div className="bg-[#D4AF37]/20 text-[#D4AF37] text-xs font-medium px-3 py-1 rounded-full">
                  {selectedMajor}
                </div>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Focus Area</p>
                  <p className="text-[#0F392B] font-medium">{profile.focus}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Key Achievement</p>
                  <p className="text-gray-600">{profile.achievement}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Interests & Activities</p>
                  <p className="text-gray-600">{profile.interests}</p>
                </div>
                <div className="pt-3 border-t border-gray-100 flex items-center justify-between">
                  <p className="text-sm text-gray-400 italic">Profile anonymized for preview</p>
                  <Link to="/sign-up">
                    <Button size="sm" className="bg-[#0F392B] hover:bg-[#0F392B]/90 text-white" data-testid="bridge-preview-cta">
                      Get Matched <ArrowRight className="ml-1 h-3 w-3" />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          )}
        </AnimSection>
      </div>
    </section>
  );
}

/* ─── SECTION 9: NATIONAL / MISSION ─── */
function MissionSection() {
  return (
    <section className="py-24 sm:py-32 bg-white" data-testid="elite-mission">
      <div className="max-w-3xl mx-auto px-6 text-center">
        <AnimSection>
          <p className="text-[#D4AF37] uppercase tracking-[0.2em] text-xs font-medium mb-4">Our Mission</p>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#0F392B] mb-8" style={{ fontFamily: "'Playfair Display', serif" }}>
            This Is Bigger Than College
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed mb-6">
            The college admissions system is one of the most powerful sorting mechanisms in American life.
            Those with access to strategic guidance rise. Those without it are left to navigate alone.
          </p>
          <p className="text-lg text-gray-600 leading-relaxed mb-10">
            This isn't about unfair advantage. It's about equal access to the strategies that have always
            existed — but were only available to those who could afford to find them.
          </p>
          <div className="border-l-4 border-[#D4AF37] pl-6 py-2 text-left max-w-xl mx-auto">
            <p className="text-xl text-[#0F392B] font-medium italic" style={{ fontFamily: "'Playfair Display', serif" }}>
              "If we're going to move forward as a country, we don't separate potential — we connect it."
            </p>
          </div>
        </AnimSection>
      </div>
    </section>
  );
}

/* ─── SECTION 10: FINAL CTA ─── */
function FinalCTASection() {
  return (
    <section className="py-24 sm:py-32 bg-[#0F392B] relative" data-testid="elite-final-cta">
      <div className="max-w-3xl mx-auto px-6 text-center">
        <AnimSection>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
            Let's Build Your Path — The Right Way
          </h2>
          <p className="text-white/50 text-lg mb-10 max-w-xl mx-auto">
            Whether you're targeting the Ivy League or building your own elite path, the strategy starts here.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
            <Link to="/sign-up">
              <Button size="lg" className="bg-[#D4AF37] hover:bg-[#C5A028] text-[#0F392B] font-semibold px-8 h-13 text-base" data-testid="final-start-strategy-btn">
                Start My Strategy <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <a href="tel:2027313100">
              <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 px-8 h-13 text-base" data-testid="final-talk-advisor-btn">
                Talk to an Advisor <Phone className="ml-2 h-4 w-4" />
              </Button>
            </a>
          </div>
          <p className="text-white/40 text-sm italic">
            Pick up the phone. Let's talk about your future.
          </p>
          <p className="text-white/30 text-xs mt-2">
            (202) 731-3100 &nbsp;|&nbsp; taimur@ivyleaguehelp.com
          </p>
        </AnimSection>
      </div>
    </section>
  );
}

/* ─── STICKY MOBILE CTA ─── */
function StickyCTA() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => setVisible(window.scrollY > 600);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 sm:hidden bg-white/95 backdrop-blur-xl border-t border-gray-200 p-3" data-testid="sticky-cta">
      <Link to="/sign-up" className="block">
        <Button className="w-full bg-[#D4AF37] hover:bg-[#C5A028] text-[#0F392B] font-semibold h-12" data-testid="sticky-cta-btn">
          Build My Plan <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </Link>
    </div>
  );
}

/* ─── MAIN PAGE ─── */
export default function EliteStrategyPage() {
  return (
    <>
      <SEO
        title="Elite Strategy. Any Path. | Ivy League Consulting"
        description="You don't need an Ivy League acceptance to build an Ivy League future. Get access to elite-level strategy, mentorship, and the Ivy Bridge Program."
        keywords="college strategy, admissions consulting, Ivy Bridge Program, college planning, transfer strategy, state school optimization"
      />
      <MarketingNav />
      <main>
        <HeroSection />
        <TentSection />
        <RealitySection />
        <StrategySection />
        <PathwaysSection />
        <PathwayQuiz />
        <OpportunityAtlasSection />
        <BridgeSection />
        <BenefitsSection />
        <IvyBridgeSection />
        <IvyBridgePreview />
        <MissionSection />
        <FinalCTASection />
      </main>
      <MarketingFooter />
      <StickyCTA />
    </>
  );
}
